import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { RESILIENCE_FILTERS, tTestResult } from "@/lib/api";
import { CritBadge } from "@/components/shared/Badges";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  ShieldAlert, GitCompare, CheckCircle2, HelpCircle, ChevronDown, Server, Boxes,
  ArrowRight, AlertTriangle, Activity, Clock,
} from "lucide-react";

const KPIS = [
  { key: "spof_potentiel", label: "SPOF potentiels", icon: ShieldAlert, tone: "text-orange-400" },
  { key: "redondance_partielle", label: "Redondances partielles", icon: GitCompare, tone: "text-yellow-400" },
  { key: "alt_non_verifiee", label: "Alternatives non vérifiées", icon: HelpCircle, tone: "text-orange-300" },
  { key: "redondance_verifiee", label: "Redondances vérifiées", icon: CheckCircle2, tone: "text-green-400" },
  { key: "resilience_degradee", label: "Résilience dégradée", icon: Activity, tone: "text-yellow-400" },
];

function DrillDialog({ open, onOpenChange, title, rows, onNavigate }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-2xl" data-testid="resilience-drill-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">{title}</DialogTitle>
          <DialogDescription>Éléments réellement à l'origine de cet indicateur (moteur de résilience structurelle).</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[55vh] overflow-y-auto cros-scroll py-1">
          {(!rows || rows.length === 0) && <p className="text-sm text-muted-foreground">Aucun élément.</p>}
          {rows?.map((e, i) => (
            <div key={i} className="border border-border rounded-sm p-3" data-testid={`drill-row-${e.asset_id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <button className="flex items-center gap-2 font-medium text-sm hover:text-primary" onClick={() => onNavigate(`/assets/${e.asset_id}`)}>
                  <Server className="h-3.5 w-3.5 text-muted-foreground" /> {e.asset_name}
                  {e.criticality && <CritBadge value={e.criticality} />}
                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                </button>
                <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary" onClick={() => onNavigate(`/services/${e.service_id}`)}>
                  <Boxes className="h-3.5 w-3.5" /> {e.service_name}
                </button>
              </div>
              {e.reason && <p className="mt-1.5 text-[11px] text-muted-foreground">{e.reason}</p>}
              {e.common_domains?.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {e.common_domains.map((c) => <Badge key={c.id} variant="outline" className="rounded-sm text-[10px] border-yellow-500/40 text-yellow-300">{c.type_label} : {c.name}</Badge>)}
                </div>
              )}
              {e.last_test && <p className="mt-1 text-[11px]">Dernier test : <span className={e.last_test.result === "reussi" ? "text-green-400" : e.last_test.result === "echoue" ? "text-red-400" : "text-yellow-400"}>{tTestResult(e.last_test.result)}</span></p>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WatchItem({ s, onNavigate }) {
  const [open, setOpen] = useState(false);
  const lvlCls = s.level === "Élevée" ? "text-orange-400 border-orange-500/40" : s.level === "Modérée" ? "text-yellow-400 border-yellow-500/40" : "text-muted-foreground border-border";
  return (
    <div className="px-6 py-3 border-b border-border last:border-0" data-testid={`watch-service-${s.service_id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button className="flex items-center gap-2 text-sm font-medium hover:text-primary" onClick={() => onNavigate(`/services/${s.service_id}`)}>
          <Boxes className="h-3.5 w-3.5 text-muted-foreground" /> {s.name} <ArrowRight className="h-3 w-3 text-muted-foreground" />
        </button>
        <div className="flex items-center gap-2">
          {s.criticality && <CritBadge value={s.criticality} />}
          <Badge variant="outline" className={`rounded-sm text-[10px] ${lvlCls}`}>Fragilité {s.level.toLowerCase()}</Badge>
          <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={() => setOpen(!open)} data-testid={`watch-why-${s.service_id}`}>
            <HelpCircle className="h-3.5 w-3.5" /> Pourquoi ? <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
          </Button>
        </div>
      </div>
      {open && (
        <ul className="mt-2 space-y-1 text-[11px] text-muted-foreground border-l-2 border-border pl-3">
          {s.criteria.map((c, i) => <li key={i}>• {c}</li>)}
        </ul>
      )}
    </div>
  );
}

export default function ResilienceSummary() {
  const navigate = useNavigate();
  const [drill, setDrill] = useState({ open: false, title: "", rows: [] });
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard-resilience"],
    queryFn: async () => (await api.get("/dashboard/resilience")).data,
  });

  if (isLoading || !data) return null;
  const s = data.summary;
  const openDrill = (key, label) => setDrill({ open: true, title: label, rows: data.elements[key] || [] });

  return (
    <div className="space-y-4" data-testid="resilience-summary">
      <div className="flex items-center justify-between">
        <h3 className="font-head font-semibold flex items-center gap-2"><Activity className="h-4 w-4 text-primary" /> Résilience structurelle</h3>
        <span className="text-[11px] text-muted-foreground italic">{data.note}</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {KPIS.map((k) => (
          <button key={k.key} onClick={() => openDrill(k.key, k.label)} data-testid={`resil-kpi-${k.key}`}
            className="border border-border bg-card p-4 rounded-sm text-left hover:border-primary/50 transition-colors">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-mono uppercase tracking-[0.1em] text-muted-foreground leading-tight">{k.label}</span>
              <k.icon className={`h-4 w-4 ${k.tone}`} />
            </div>
            <div className="font-mono text-3xl font-semibold">{s[k.key] ?? 0}</div>
          </button>
        ))}
      </div>

      <div className="border border-border bg-card rounded-sm p-4 flex items-center justify-between" data-testid="resil-critical-spof">
        <span className="text-sm text-muted-foreground">Services critiques présentant au moins un SPOF potentiel</span>
        <span className="font-mono text-2xl font-semibold text-orange-400">{s.critical_services_with_spof ?? 0}</span>
      </div>

      {/* Alertes de fraîcheur — seulement si une politique est définie */}
      {data.policy_enabled && data.freshness_alerts.length > 0 && (
        <div className="border border-yellow-500/30 bg-yellow-500/5 rounded-sm p-4" data-testid="freshness-alerts">
          <div className="flex items-center gap-2 mb-3 text-yellow-400">
            <Clock className="h-4 w-4" />
            <span className="font-head font-semibold">Alertes de fraîcheur — {data.freshness_alerts.length}</span>
          </div>
          <ul className="space-y-2">
            {data.freshness_alerts.map((a) => (
              <li key={a.alt_id} className="text-sm" data-testid={`freshness-alert-${a.alt_id}`}>
                <span className={`font-medium ${a.level === "expire" ? "text-red-400" : "text-yellow-400"}`}>{a.message}</span>
                <span className="text-muted-foreground"> — alternative « {a.alternative_name} », dernier test {a.last_test ? `${tTestResult(a.last_test.result)} (` : ""}{a.age_days != null ? `${a.age_days} j` : "—"}{a.last_test ? ")" : ""}, seuil {a.threshold_days ?? "—"} j.</span>
                {a.services?.length > 0 && <span className="text-[11px] text-muted-foreground"> Service(s) : {a.services.map((x) => x.name).join(", ")}.</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Services à surveiller */}
      <div className="border border-border bg-card rounded-sm">
        <h4 className="font-head font-semibold p-6 pb-4 border-b border-border flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-yellow-400" /> Services à surveiller (fragilité structurelle)
        </h4>
        <div data-testid="services-to-watch">
          {data.services_to_watch.length === 0 && <p className="p-6 text-sm text-muted-foreground">Aucun service ne présente de fragilité structurelle connue.</p>}
          {data.services_to_watch.map((sv) => <WatchItem key={sv.service_id} s={sv} onNavigate={navigate} />)}
        </div>
      </div>

      <DrillDialog open={drill.open} onOpenChange={(v) => setDrill((d) => ({ ...d, open: v }))} title={drill.title} rows={drill.rows} onNavigate={(to) => { setDrill((d) => ({ ...d, open: false })); navigate(to); }} />
    </div>
  );
}
